// Printing: a receipt goes to the 80 mm thermal printer, an invoice or agreement to
// regular 8.5 x 11 paper, a sticker to the DYMO label printer (a bought phone's label is the
// same 2.25 x 1.25 in size). The page being printed is drawn into #print-root so the app
// itself never ends up on paper. Several print jobs in a row (a receipt, then a sticker) open
// one print window after the other.
//
// Thermal: one print can hold several tickets (a repair's customer copy and the technician's
// ticket). Each ticket is its own page, 70.6 mm wide (RepairDesk's receipt width) and exactly as
// tall as the ticket, with no page margins (so the browser has no room to add its date, web address and page numbers), and
// the printer cuts after each page.
import { Fragment, createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { code128Bars } from '../../shared/barcode';
import { brand } from './brand';
import { markDemoStep } from '../lib/demo';

/**
 * sticker: DYMO 30334 (2¼ × 1¼ in); sticker-wide: DYMO 30252 (3½ × 1⅛ in). A bought phone's
 * label: phone-label (Brother DK-1202, 62 × 100 mm), phone-label-4 (DYMO 30256, 2-5/16 × 4 in)
 * or label (2¼ × 1¼ in). The technician's repair label prints sideways on the same labels:
 * tech-wide (Brother, 100 × 62 mm) or tech-wide-4 (DYMO 30256, 4 × 2-5/16 in). The technician's
 * label: tech-label, 89 × 29 mm on the DYMO LabelWriter (RepairDesk's ticket label size). The
 * price sticker for a phone on sale: sticker-3x2, a 3 × 2 in label on the Rollo, or sticker-4x3, a
 * 4 × 3 in label on the Rollo (the sticker a third bigger, sideways across the label).
 */
export type PaperSize = 'thermal' | 'letter' | 'label' | 'sticker' | 'sticker-wide' | 'sticker-3x2' | 'sticker-2x3' | 'sticker-4x3' | 'sticker-2x2' | 'phone-label' | 'phone-label-4' | 'tech-wide' | 'tech-wide-4' | 'tech-label' | 'tech-label-4';
/** Prints one document, or several in one print (thermal: each its own page, cut between them). */
export type PrintFn = (doc: ReactNode | ReactNode[], paper?: PaperSize) => void;

const Ctx = createContext<PrintFn>(() => window.print());

/** The receipt font (RepairDesk's): loaded before a ticket is measured, so it's measured as it prints. */
const RECEIPT_FONTS = ["400 12px 'DejaVu Sans Condensed'", "700 12px 'DejaVu Sans Condensed'"];
const loadReceiptFonts = () => (typeof document !== 'undefined' && document.fonts ? RECEIPT_FONTS.map((f) => document.fonts.load(f).catch(() => null)) : []);
// Fetched when the app starts, so the first print doesn't wait for it.
loadReceiptFonts();

/** What the demo's preview calls each paper. */
const PAPER_NAMES: Partial<Record<PaperSize, string>> = {
  thermal: 'Receipt printer (80 mm thermal)', letter: 'Letter paper', label: 'Label (2¼ × 1¼ in)', sticker: 'Sticker (2¼ × 1¼ in)', 'sticker-wide': 'Sticker (3½ × 1⅛ in)',
  'sticker-3x2': 'Price sticker (3 × 2 in)', 'sticker-2x3': 'Price sticker (2 × 3 in)', 'sticker-4x3': 'Price sticker (4 × 3 in)', 'sticker-2x2': 'Price sticker (2 × 2 in)',
  'phone-label': 'Phone label (62 × 100 mm)', 'phone-label-4': 'Phone label (2-5/16 × 4 in)', 'tech-wide': 'Technician label (100 × 62 mm)', 'tech-wide-4': 'Technician label (4 × 2-5/16 in)',
  'tech-label': 'Technician label (89 × 29 mm)', 'tech-label-4': 'Technician label (4 in)',
};

export function PrintProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<{ docs: ReactNode[]; paper: PaperSize; n: number }[]>([]);
  const seq = useRef(0);
  const print = useCallback<PrintFn>((doc, paper = 'thermal') => setJobs((js) => [...js, { docs: Array.isArray(doc) ? doc : [doc], paper, n: ++seq.current }]), []);
  const job = jobs[0] ?? null;
  // The demo: the printout shows on the screen instead of a print window (nothing is printed).
  const [preview, setPreview] = useState<{ n: number; paper: PaperSize; close: () => void } | null>(null);

  useEffect(() => {
    if (!job) return;
    document.body.classList.add('printing');
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      document.body.classList.remove('printing');
      document.getElementById('print-root')?.classList.remove('preview');
      setPreview(null);
      // A moment between print windows so the browser has closed the last one.
      setTimeout(() => setJobs((js) => js.slice(1)), 300);
    };
    if (brand.demo) {
      const root = document.getElementById('print-root');
      root?.classList.add('preview');
      setPreview({ n: job.n, paper: job.paper, close: done });
      markDemoStep('receipt');
      return () => { root?.classList.remove('preview'); setPreview(null); };
    }
    window.addEventListener('afterprint', done, { once: true });
    // Let pictures (logo, STORE OS mark) and the font load before measuring and opening the print
    // window, so a ticket is measured exactly as it prints.
    const root = document.getElementById('print-root');
    const imgs = [...(root?.querySelectorAll('img') ?? [])].filter((i) => !i.complete);
    let cancelled = false;
    let fallback: ReturnType<typeof setTimeout> | undefined;
    let unfit = () => {};
    let unpage = () => {};
    Promise.race([
      Promise.all([
        ...imgs.map((i) => new Promise((r) => { i.addEventListener('load', r, { once: true }); i.addEventListener('error', r, { once: true }); })),
        ...loadReceiptFonts(),
        document.fonts?.ready,
      ]),
      new Promise((r) => setTimeout(r, 4000)),
    ]).then(() => {
      if (cancelled) return;
      // Thermal paper is cut right after the last line: each page is exactly as long as its ticket.
      if (job.paper === 'thermal' && root) unfit = fitThermalPages(root);
      // A sticker: the page is the label, with no margins, for the whole print (see setPageSize).
      const size = PAGE_SIZES[job.paper];
      if (size) unpage = setPageSize(size);
      window.print();
      // Some tablets never say the print window closed; don't let the next print wait forever.
      fallback = setTimeout(done, 20_000);
    });
    return () => {
      cancelled = true;
      clearTimeout(fallback);
      unfit();
      unpage();
      window.removeEventListener('afterprint', done);
    };
  }, [job]);

  const root = typeof document !== 'undefined' ? document.getElementById('print-root') : null;
  return (
    <Ctx.Provider value={print}>
      {children}
      {preview ? createPortal(
        <div className="print-preview-bar" role="dialog" aria-label="Print preview">
          <span className="ppb-t"><strong>Print preview</strong> · {PAPER_NAMES[preview.paper] ?? preview.paper}. In the demo nothing is printed; your own printers would print this.</span>
          <button type="button" className="ppb-close" onClick={preview.close}>Close</button>
        </div>,
        document.body,
      ) : null}
      {job && root ? createPortal(
        <div key={job.n} className={job.paper}>
          {job.paper === 'thermal'
            ? job.docs.map((d, i) => <div key={i} className="ticket">{d}</div>)
            : job.docs.map((d, i) => <Fragment key={i}>{d}</Fragment>)}
        </div>,
        root,
      ) : null}
    </Ctx.Provider>
  );
}

export function usePrint(): PrintFn {
  return useContext(Ctx);
}

/**
 * Price sticker labels: the page is the label, with no margins, set for the WHOLE print (an unnamed
 * @page rule, not only the named page) right before the print window opens. With no margins the
 * print window has no room for its date, title, web address and page numbers, and the label
 * can't spill onto a second one. Taken away again after printing.
 */
const PAGE_SIZES: Partial<Record<PaperSize, string>> = { 'sticker-3x2': '3in 2in', 'sticker-2x3': '2in 3in', 'sticker-4x3': '4in 3in', 'sticker-2x2': '2in 2in' };
function setPageSize(size: string): () => void {
  const style = document.createElement('style');
  style.setAttribute('data-print-page', '');
  style.textContent = `@page { size: ${size}; margin: 0; }`;
  document.head.appendChild(style);
  return () => style.remove();
}

/** A thermal page's width: RepairDesk's thermal receipt width, 70.6 mm (200 pt). */
export const THERMAL_WIDTH_MM = 70.6;
/** The longest ticket that still fits on one receipt paper (drivers often end a page at 297 mm). */
export const THERMAL_MAX_MM = 288;

/**
 * Page height for a thermal ticket that measures `px` CSS pixels (its 3 mm top and bottom padding
 * included): rounded up to the next half millimetre plus half a millimetre, so the last line never
 * spills onto a blank second page. Never shorter than 82 mm: a page wider than it is tall gets
 * turned sideways by some printer drivers.
 */
export function thermalPageMm(px: number): number {
  return Math.max(82, Math.ceil(((px * 25.4) / 96 + 0.5) * 2) / 2);
}

/**
 * Sizes each thermal ticket's page to the ticket, right before the print window opens: measured
 * at its printed width (70.6 mm, like RepairDesk) with the same styles as on paper, then given its
 * own named page (`@page thermal-1 { size: 70.6mm <height>; margin: 0 }`). The page has no margins, so the print
 * window has no room for its date, web address and page numbers. Returns a function that puts
 * things back after printing.
 */
export function fitThermalPages(root: HTMLElement): () => void {
  const job = root.firstElementChild as HTMLElement | null;
  const tickets = job ? ([...job.children] as HTMLElement[]) : [];
  if (!tickets.length) return () => {};
  root.classList.add('measure');
  // A ticket longer than one receipt paper is made tighter (smaller logo and fine print) so it
  // comes out on ONE paper: printer drivers often end a page at 297 mm and start a second paper.
  const heights = tickets.map((t) => {
    let h = t.getBoundingClientRect().height;
    for (const tighter of ['tight', 'tighter']) {
      if (!h || thermalPageMm(h) <= THERMAL_MAX_MM) break;
      t.classList.add(tighter);
      h = t.getBoundingClientRect().height;
    }
    return h;
  });
  root.classList.remove('measure');
  const rules: string[] = [];
  tickets.forEach((t, i) => {
    if (!heights[i]) return; // not measurable: it keeps the plain thermal page
    const name = `thermal-${i + 1}`;
    rules.push(`@page ${name} { size: ${THERMAL_WIDTH_MM}mm ${thermalPageMm(heights[i])}mm; margin: 0; }`);
    t.style.setProperty('page', name);
  });
  const style = document.createElement('style');
  style.setAttribute('data-thermal-page', '');
  style.textContent = rules.join('\n');
  document.head.appendChild(style);
  return () => {
    style.remove();
    for (const t of tickets) {
      t.style.removeProperty('page');
      t.classList.remove('tight', 'tighter');
    }
  };
}

// ---------------------------------------------------------------- Code 128 barcode

export { code128Values } from '../../shared/barcode';

export function Barcode({ value, height = 44 }: { value: string; height?: number }) {
  const { bars, total } = code128Bars(value);
  return (
    <svg viewBox={`0 0 ${total} ${height}`} preserveAspectRatio="none" role="img" aria-label={`Barcode ${value}`} shapeRendering="crispEdges">
      <rect width={total} height={height} fill="#fff" />
      {bars.map((b, i) => <rect key={i} x={b.x} y={0} width={b.w} height={height} fill="#000" />)}
    </svg>
  );
}
